/**
 * Rich message input component with auto-expanding textarea, keyboard shortcuts,
 * and visual feedback for submission states.
 */
import { Loader2, Send } from "lucide-react";
import {
	useRef,
	useEffect,
	useCallback,
	type KeyboardEvent,
	type ChangeEvent,
	useState,
} from "react";

import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";

/** Height of a single line in pixels (for auto-expand calculation) */
const LINE_HEIGHT = 24;
/** Minimum number of rows */
const MIN_ROWS = 1;
/** Maximum number of rows before scrolling */
const MAX_ROWS = 10;
/** Character count warning threshold */
const CHAR_COUNT_SHOW_THRESHOLD = 1000;
/** Character count warning threshold (approaching limit) */
const CHAR_COUNT_WARN_THRESHOLD = 4000;

interface MessageInputProps {
	/** Callback when user submits a message */
	onSubmit: (message: string) => void;
	/** Whether the component is in loading state (e.g., waiting for response) */
	isLoading?: boolean;
	/** Whether the component is disabled */
	isDisabled?: boolean;
	/** Placeholder text */
	placeholder?: string;
	/** Whether to auto-focus on mount */
	autoFocus?: boolean;
	/** Whether to show character count */
	showCharacterCount?: boolean;
}

/**
 * Message input component with auto-expanding textarea and keyboard shortcuts.
 *
 * Keyboard shortcuts:
 * - Enter: Submit message
 * - Shift+Enter: Insert newline
 * - Escape: Clear input and blur
 * @param props - Component props
 * @param props.onSubmit - Callback when user submits a message
 * @param props.isLoading - Whether the component is in loading state
 * @param props.isDisabled - Whether the component is disabled
 * @param props.placeholder - Placeholder text for the textarea
 * @param props.autoFocus - Whether to auto-focus on mount
 * @param props.showCharacterCount - Whether to show character count indicator
 */
export function MessageInput({
	onSubmit,
	isLoading = false,
	isDisabled = false,
	placeholder = "Send a message...",
	autoFocus = true,
	showCharacterCount = true,
}: MessageInputProps) {
	const textareaRef = useRef<HTMLTextAreaElement>(null);
	const [value, setValue] = useState("");

	const disabled = isLoading || isDisabled;
	const isEmpty = value.trim().length === 0;
	const charCount = value.length;
	const showCount = showCharacterCount && charCount > CHAR_COUNT_SHOW_THRESHOLD;
	const isNearLimit = charCount >= CHAR_COUNT_WARN_THRESHOLD;

	/**
	 * Adjusts textarea height based on content.
	 * Expands from MIN_ROWS to MAX_ROWS, then enables scrolling.
	 */
	const adjustHeight = useCallback(() => {
		const textarea = textareaRef.current;
		if (!textarea) return;

		// Reset height to measure scrollHeight accurately
		textarea.style.height = "auto";

		const minHeight = LINE_HEIGHT * MIN_ROWS;
		const maxHeight = LINE_HEIGHT * MAX_ROWS;
		const scrollHeight = textarea.scrollHeight;

		// Clamp height between min and max
		const newHeight = Math.min(Math.max(scrollHeight, minHeight), maxHeight);
		textarea.style.height = `${newHeight}px`;
	}, []);

	// Adjust height when value changes
	useEffect(() => {
		adjustHeight();
	}, [value, adjustHeight]);

	// Auto-focus on mount
	useEffect(() => {
		if (autoFocus && textareaRef.current) {
			textareaRef.current.focus();
		}
	}, [autoFocus]);

	/**
	 * Handles form submission.
	 * Trims whitespace, prevents empty submissions, clears input after submit.
	 */
	const handleSubmit = useCallback(() => {
		const trimmedValue = value.trim();
		if (trimmedValue.length === 0 || disabled) return;

		onSubmit(trimmedValue);
		setValue("");

		// Refocus after submit
		textareaRef.current?.focus();
	}, [value, disabled, onSubmit]);

	/**
	 * Handles keyboard events.
	 * - Enter without modifiers: Submit
	 * - Shift+Enter: Insert newline (default behavior)
	 * - Escape: Clear and blur
	 */
	const handleKeyDown = useCallback(
		(e: KeyboardEvent<HTMLTextAreaElement>) => {
			if (e.key === "Enter" && !e.shiftKey && !e.ctrlKey && !e.metaKey && !e.altKey) {
				e.preventDefault();
				handleSubmit();
			}
			else if (e.key === "Escape") {
				e.preventDefault();
				setValue("");
				textareaRef.current?.blur();
			}
		},
		[handleSubmit]
	);

	/**
	 * Handles input change.
	 */
	const handleChange = useCallback((e: ChangeEvent<HTMLTextAreaElement>) => {
		setValue(e.target.value);
	}, []);

	return (
		<div className="flex flex-col gap-1">
			<div className="flex items-end gap-2">
				{/* Auto-expanding textarea */}
				<textarea
					ref={textareaRef}
					value={value}
					onChange={handleChange}
					onKeyDown={handleKeyDown}
					disabled={disabled}
					placeholder={placeholder}
					rows={MIN_ROWS}
					className={cn(
						// Base styles from shadcn/ui Textarea
						"flex w-full rounded-md border border-input bg-transparent px-3 py-2",
						"text-base shadow-sm md:text-sm",
						"placeholder:text-muted-foreground",
						"focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring",
						"disabled:cursor-not-allowed disabled:opacity-50",
						// Custom styles for auto-expand
						"resize-none overflow-y-auto",
						"min-h-[40px]"
					)}
					style={{
						lineHeight: `${LINE_HEIGHT}px`,
					}}
				/>

				{/* Submit button */}
				<Button
					type="button"
					size="icon"
					onClick={handleSubmit}
					disabled={disabled || isEmpty}
					aria-label={isLoading ? "Sending..." : "Send message"}
				>
					{isLoading ? (
						<Loader2 className="h-4 w-4 animate-spin" />
					) : (
						<Send className="h-4 w-4" />
					)}
				</Button>
			</div>

			{/* Character count indicator */}
			{showCount && (
				<div
					className={cn(
						"text-xs text-right",
						isNearLimit ? "text-red-400" : "text-muted-foreground"
					)}
				>
					{charCount.toLocaleString()} characters
					{isNearLimit && " (approaching limit)"}
				</div>
			)}
		</div>
	);
}
